// src/utils/auth.js
// Central auth utilities + a fetch wrapper that auto-redirects on expired/invalid JWT.

const LOGIN_PATH = "/login"; // change if your login route differs

const TOKEN_KEYS = ["jwtToken", "token"];

export function getToken() {
  try {
    for (const k of TOKEN_KEYS) {
      const v = localStorage.getItem(k);
      if (v) return v;
    }
    for (const k of TOKEN_KEYS) {
      const v = sessionStorage.getItem(k);
      if (v) return v;
    }
    // optional global
    if (typeof window !== "undefined" && window.WHITEINTEL_TOKEN) {
      return window.WHITEINTEL_TOKEN;
    }
  } catch {}
  return "";
}

export function setToken(token) {
  try {
    localStorage.setItem("token", token);
  } catch {}
}

export function clearAuth() {
  try {
    TOKEN_KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
  } catch {}
}

function decodeJwt(token) {
  try {
    const base = token.split(".")[1];
    if (!base) return null;
    // handle base64url
    const pad = "=".repeat((4 - (base.length % 4)) % 4);
    const b64 = (base + pad).replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function isTokenExpired(token, skewSeconds = 30) {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) return false; // if no exp claim, assume non-expiring
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + skewSeconds;
}

export function redirectToLogin() {
  clearAuth();
  // Optional: preserve where the user was
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.replace(`${LOGIN_PATH}?next=${next}`);
}

// Whitelist of in-app paths the post-login `?next=` redirect is allowed to land on.
// Anything outside this list (or any external/protocol-relative URL) is rejected
// and falls back to the caller's default. Login/auth-only paths are intentionally
// excluded to avoid redirect loops.
const SAFE_NEXT_PATHS = [
  "/",
  "/global-stats",
  "/dashboard",
  "/search",
  "/global-search",
  "/saved-incidents",
  "/investigations",
  "/watchlist-events",
  "/brand-protection",
  "/watchlists",
  "/threat-feeds",
  "/organization-settings",
  "/profile",
  "/webhooks",
  "/upgrades",
  "/contact",
  "/payment-fraud",
  "/supplier-security",
];

/**
 * Read `?next=` from the current URL and return a same-origin path safe to redirect
 * to after login. Rejects external URLs, protocol-relative URLs, backslash tricks,
 * decoding failures, and any path not on the whitelist. Returns the supplied
 * fallback when the value is missing or unsafe.
 */
export function getSafeNext(fallback = "/") {
  if (typeof window === "undefined") return fallback;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("next");
    if (!raw) return fallback;

    let decoded;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      return fallback;
    }

    // Must be an absolute, same-origin path. Reject "//evil" (protocol-relative),
    // backslash tricks, and anything that doesn't begin with a single "/".
    if (!decoded.startsWith("/")) return fallback;
    if (decoded.startsWith("//")) return fallback;
    if (decoded.includes("\\")) return fallback;

    // Re-parse against current origin so we can enforce same-origin defensively.
    let parsed;
    try {
      parsed = new URL(decoded, window.location.origin);
    } catch {
      return fallback;
    }
    if (parsed.origin !== window.location.origin) return fallback;

    // Whitelist by pathname (exact match, or whitelist entry as a path prefix
    // ending at a "/" boundary so "/upgrades-malicious" doesn't match "/upgrades").
    const pathname = parsed.pathname || "/";
    const allowed = SAFE_NEXT_PATHS.some(
      (p) => pathname === p || pathname.startsWith(p === "/" ? "/" : p + "/")
    );
    if (!allowed) return fallback;

    return pathname + parsed.search + parsed.hash;
  } catch {
    return fallback;
  }
}

export function ensureAuthOrRedirect() {
  const t = getToken();
  if (!t || isTokenExpired(t)) {
    redirectToLogin();
    return false;
  }
  return true;
}

/**
 * Schedule an automatic logout a few seconds before token expiry.
 * Call this once on app start and anytime you replace the token.
 */
let _expiryTimer = null;
export function attachExpiryTimer(skewSeconds = 5) {
  if (_expiryTimer) {
    clearTimeout(_expiryTimer);
    _expiryTimer = null;
  }
  const t = getToken();
  const payload = t ? decodeJwt(t) : null;
  if (!payload || !payload.exp) return;

  const nowMs = Date.now();
  const fireAtMs = payload.exp * 1000 - skewSeconds * 1000;
  const delay = Math.max(0, fireAtMs - nowMs);

  _expiryTimer = setTimeout(() => {
    // optional: toast/alert
    // alert("Your session has expired. Please sign in again.");
    redirectToLogin();
  }, delay);
}

/**
 * fetch wrapper that auto-injects Authorization header
 * and redirects when it detects auth failure.
 */
export async function apiFetch(url, options = {}) {
  const token = getToken();
  const headers = new Headers(options.headers || {});
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (!headers.has("Content-Type") && options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(url, { ...options, headers });

  // Hard auth failures via status
  if ([401, 403, 419, 440].includes(res.status)) {
    redirectToLogin();
    // throw to stop callers from continuing
    throw new Error(`AUTH_REDIRECT_${res.status}`);
  }

  // Heuristic: many APIs return 200 with JSON error bodies
  // We "peek" the JSON safely via clone()
  try {
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      const clone = res.clone();
      const data = await clone.json();
      const errMsg = String(
        data?.error || data?.message || data?.status || ""
      ).toLowerCase();
      const errCode = String(data?.error_code || data?.code || "").toLowerCase();
      const looksExpired =
        errCode.includes("token") ||
        errCode.includes("auth") ||
        errMsg.includes("token") ||
        errMsg.includes("expired") ||
        errMsg.includes("unauthorized");
      if (looksExpired) {
        redirectToLogin();
        throw new Error("AUTH_REDIRECT_BODY");
      }
    }
  } catch {
    // ignore body-peek errors; still return original response
  }

  return res;
}
